import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Link2 } from "lucide-react";

/**
 * ImageUpload - reusable drag-and-drop image uploader with URL fallback.
 *
 * Props:
 *   value: current image URL (string)
 *   onChange: (url) => void
 *   kind: "course-cover" | "faculty" | "misc"
 *   label: optional label below the preview
 */
export default function ImageUpload({ value, onChange, kind = "misc", label = "Drop an image here or click to browse" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
      toast.error("Only PNG, JPG, GIF or WEBP images");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (max 8 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/upload-image?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };
  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  return (
    <div className="space-y-2" data-testid="image-upload">
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="preview"
            className="h-32 w-auto rounded-xl object-cover border border-[var(--border-warm)] bg-[var(--muted-surface)]"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <button
            type="button"
            className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[var(--terracotta)] text-white grid place-items-center shadow"
            onClick={() => onChange("")}
            title="Remove image"
            data-testid="image-upload-remove"
          >
            <X size={14}/>
          </button>
        </div>
      ) : null}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-all ${dragging ? "border-[var(--terracotta)] bg-[var(--muted-surface)]" : "border-[var(--border-warm)] bg-white hover:border-[var(--navy)]"}`}
        data-testid="image-upload-dropzone"
      >
        {uploading ? (
          <div className="text-sm text-zinc-600 inline-flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-[var(--terracotta)] border-t-transparent rounded-full animate-spin"/>
            Uploading…
          </div>
        ) : (
          <>
            <ImageIcon size={22} className="mx-auto text-zinc-400 mb-1"/>
            <div className="text-sm font-semibold">{value ? "Replace image" : label}</div>
            <div className="text-xs text-zinc-500 mt-1">PNG · JPG · WEBP · GIF - up to 8 MB</div>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFileInput} data-testid="image-upload-input"/>
      </div>

      <div>
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-[var(--terracotta)] inline-flex items-center gap-1"
          onClick={() => setShowUrl((v) => !v)}
          data-testid="image-upload-url-toggle"
        >
          <Link2 size={12}/> {showUrl ? "Hide URL" : "…or paste a URL instead"}
        </button>
        {showUrl && (
          <input
            type="url"
            className="field mt-1"
            placeholder="https://.../image.jpg"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid="image-upload-url-input"
          />
        )}
      </div>
    </div>
  );
}
